import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';

import {
  computeWeightDifferenceCheck,
  confirmWeightDifferenceCheck,
  efConfirmWeightDifferenceCheck,
  fetchWeightDifferenceChecks,
} from './api';
import type { ComputeWeightDifferenceRequest, WeightDifferenceCheckDto } from './types';

const KEY = 'weight-difference-checks' as const;

/** Batch-scoped list of weight-difference checks. */
export const useWeightDifferenceChecks = (batchId: string | undefined) => {
  return useQuery<WeightDifferenceCheckDto[], ApiClientError>({
    queryKey: [KEY, 'list', batchId],
    queryFn: () => fetchWeightDifferenceChecks(batchId!),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

/** POST record a new check for the batch. */
export const useComputeWeightDifferenceCheck = (batchId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<WeightDifferenceCheckDto, ApiClientError, ComputeWeightDifferenceRequest>({
    mutationFn: (body) => computeWeightDifferenceCheck(batchId!, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'list', batchId] }),
  });
};

/** POST DK-side confirm a check (id-scoped). */
export const useConfirmWeightDifferenceCheck = (batchId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<WeightDifferenceCheckDto, ApiClientError, string>({
    mutationFn: (id) => confirmWeightDifferenceCheck(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'list', batchId] }),
  });
};

/** POST EF-side confirm a check (id-scoped). */
export const useEfConfirmWeightDifferenceCheck = (batchId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<WeightDifferenceCheckDto, ApiClientError, string>({
    mutationFn: (id) => efConfirmWeightDifferenceCheck(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'list', batchId] }),
  });
};
